import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { useReveal } from "../lib/useReveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  inquiry_type: "creator",
  handle_or_company: "",
  message: "",
};

export const Contact = () => {
  const headRef = useReveal();
  const formRef = useReveal();
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "submitting") return;

    const trimmed = {
      name: form.name.trim(),
      email: form.email.trim(),
      inquiry_type: form.inquiry_type,
      handle_or_company: form.handle_or_company.trim(),
      message: form.message.trim(),
    };

    if (!trimmed.name || !trimmed.email || !trimmed.handle_or_company || !trimmed.message) {
      toast.error("Please fill in all fields.");
      return;
    }

    setStatus("submitting");
    try {
      await axios.post(`${API}/contact`, trimmed);
      setStatus("success");
      setForm(initial);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (err) {
      console.error(err);
      setStatus("error");
      const detail = err?.response?.data?.detail;
      let message =
        "Something went wrong. Please try again or email contact@outtarage.com.";
      if (typeof detail === "string") {
        message = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        const field = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : null;
        message = field
          ? `${String(field).replace(/_/g, " ")}: ${first.msg}`
          : first.msg || message;
      }
      toast.error(message);
    } finally {
      // reset to idle so user can submit again
      setTimeout(() => setStatus((s) => (s === "submitting" ? "idle" : s)), 200);
    }
  };

  const inputClasses =
    "w-full bg-transparent border-b border-white/15 focus:border-white text-white placeholder:text-neutral-500 px-0 py-4 text-base md:text-lg outline-none transition-colors";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#050505] py-24 md:py-32 border-t border-white/5 overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-1/3 -left-32 h-[28rem] w-[28rem] rounded-full bg-white/[0.04] blur-3xl float-slow"
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left */}
          <div ref={headRef} className="reveal lg:col-span-5">
            <span className="text-xs uppercase tracking-[0.28em] text-neutral-500 font-medium">
              07 / Contact
            </span>
            <h2
              data-testid="contact-heading"
              className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter leading-[1.02]"
            >
              Let&apos;s build
              <br />
              <span className="italic font-medium">something loud.</span>
            </h2>
            <p className="mt-8 text-base md:text-lg text-neutral-400 leading-relaxed max-w-md">
              Tell us a little about you — whether you&apos;re a creator looking for
              representation or a brand planning your next campaign.
            </p>

            <div className="mt-12 space-y-4">
              <a
                href="mailto:contact@outtarage.com"
                data-testid="contact-email-link"
                className="group flex items-center justify-between border-t border-white/10 py-4 hover:border-white/30 transition-colors"
              >
                <span className="text-neutral-400 text-sm uppercase tracking-[0.2em]">
                  Email
                </span>
                <span className="font-display text-base md:text-lg text-white group-hover:translate-x-0.5 transition-transform">
                  contact@outtarage.com
                </span>
              </a>
              <a
                href="https://instagram.com/outtarage"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-social-link"
                className="group flex items-center justify-between border-t border-white/10 py-4 hover:border-white/30 transition-colors"
              >
                <span className="text-neutral-400 text-sm uppercase tracking-[0.2em]">
                  Instagram
                </span>
                <span className="font-display text-base md:text-lg text-white group-hover:translate-x-0.5 transition-transform">
                  @outtarage
                </span>
              </a>
              <div className="flex items-center justify-between border-t border-b border-white/10 py-4">
                <span className="text-neutral-400 text-sm uppercase tracking-[0.2em]">
                  Reach
                </span>
                <span className="font-display text-base md:text-lg text-white">
                  Worldwide
                </span>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <form
            ref={formRef}
            onSubmit={submit}
            data-testid="contact-form"
            className="reveal lg:col-span-7 lg:pl-8"
            noValidate
          >
            <div className="space-y-2 mb-8">
              <Field label="Name" htmlFor="contact-name">
                <input
                  id="contact-name"
                  data-testid="contact-input-name"
                  type="text"
                  autoComplete="name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your full name"
                  className={inputClasses}
                  required
                />
              </Field>

              <Field label="Email" htmlFor="contact-email">
                <input
                  id="contact-email"
                  data-testid="contact-input-email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@email.com"
                  className={inputClasses}
                  required
                />
              </Field>

              <Field label="I am a" htmlFor="contact-type">
                <select
                  id="contact-type"
                  data-testid="contact-input-type"
                  value={form.inquiry_type}
                  onChange={update("inquiry_type")}
                  className={`${inputClasses} appearance-none cursor-pointer`}
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23a3a3a3' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 0 center",
                    backgroundSize: "18px 18px",
                    paddingRight: "32px",
                  }}
                >
                  <option value="creator" className="bg-[#0a0a0a] text-white">
                    Creator
                  </option>
                  <option value="brand" className="bg-[#0a0a0a] text-white">
                    Brand
                  </option>
                </select>
              </Field>

              <Field
                label={
                  form.inquiry_type === "brand"
                    ? "Company"
                    : "Social handle"
                }
                htmlFor="contact-handle"
              >
                <input
                  id="contact-handle"
                  data-testid="contact-input-handle"
                  type="text"
                  value={form.handle_or_company}
                  onChange={update("handle_or_company")}
                  placeholder={
                    form.inquiry_type === "brand"
                      ? "Company name"
                      : "@yourhandle"
                  }
                  className={inputClasses}
                  required
                />
              </Field>

              <Field label="Message" htmlFor="contact-message">
                <textarea
                  id="contact-message"
                  data-testid="contact-input-message"
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your project, audience, or goal."
                  className={`${inputClasses} resize-none`}
                  required
                />
              </Field>
            </div>

            <button
              type="submit"
              data-testid="contact-submit"
              disabled={status === "submitting"}
              className="group inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-neutral-200 disabled:opacity-60 disabled:cursor-not-allowed transition-all rounded-full px-8 py-4 text-sm font-medium tracking-wide"
            >
              {status === "submitting" && (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending…
                </>
              )}
              {status === "success" && (
                <>
                  <Check className="h-4 w-4" />
                  Sent
                </>
              )}
              {status !== "submitting" && status !== "success" && (
                <>
                  Send Message
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, htmlFor, children }) => (
  <label htmlFor={htmlFor} className="block group">
    <span className="text-xs uppercase tracking-[0.22em] text-neutral-500 font-medium">
      {label}
    </span>
    {children}
  </label>
);

export default Contact;
